/**
 * Painel de Avaliações (testes do GridProfile) atreladas a um candidato.
 *
 * - Lista os testes já aplicados (status: pending / in_progress / completed / failed)
 * - Botão "Aplicar novo teste" abre um Dialog com o catálogo do GridProfile
 * - Cada teste completo mostra: score, label, breakdown JSON quando aplicável,
 *   e link pro relatório PDF
 * - Botão "Sincronizar" força um GET no GridProfile caso o webhook não tenha
 *   chegado (escape hatch)
 *
 * Endpoints consumidos:
 *  - GET  /api/integrations/gridprofile/catalog
 *  - GET  /api/candidates/{id}/assessments
 *  - POST /api/candidates/{id}/assessments
 *  - POST /api/assessments/{id}/sync
 */
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Plus,
  RefreshCw,
  Sparkles,
  Brain,
  Users,
  Puzzle,
  Compass,
  Calculator,
  Languages,
  Table as TableIcon,
  FileText,
  Copy,
  ExternalLink,
  CheckCircle2,
  Clock,
  XCircle,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const ICON_MAP = {
  brain: Brain,
  users: Users,
  puzzle: Puzzle,
  compass: Compass,
  calculator: Calculator,
  languages: Languages,
  table: TableIcon,
};

// Fallback de ícones por test_id quando o GridProfile não devolve `icon`
// no catálogo. Mantém a UX visual mesmo se o provider mudar payload.
const ICON_BY_TEST_ID = {
  "disc-classic": Users,
  "big-five": Brain,
  "mbti": Puzzle,
  "eneagrama": Compass,
  "raciocinio-logico": Calculator,
  "ingles-toeic": Languages,
  "excel-avancado": TableIcon,
};

// Normaliza category vinda da API (aceita "Personalidade"/"personalidade"/
// "PERSONALIDADE"/com acento etc.) pra uma das 3 keys conhecidas do UI.
const normalizeCategory = (raw) => {
  const v = (raw || "").toString().toLowerCase().trim()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, ""); // tira acentos
  if (v.startsWith("person")) return "personalidade";
  if (v.startsWith("racioc")) return "raciocinio";
  if (v.startsWith("tec")) return "tecnico";
  return "tecnico"; // fallback seguro
};

const CATEGORY_BADGE = {
  personalidade: { label: "Personalidade", klass: "bg-violet-100 text-violet-700 border-violet-200" },
  raciocinio: { label: "Raciocínio", klass: "bg-blue-100 text-blue-700 border-blue-200" },
  tecnico: { label: "Técnico", klass: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

const STATUS_META = {
  pending: { label: "Aguardando", icon: Clock, klass: "bg-slate-100 text-slate-700 border-slate-200" },
  in_progress: { label: "Em andamento", icon: Loader2, klass: "bg-amber-100 text-amber-700 border-amber-200" },
  completed: { label: "Concluído", icon: CheckCircle2, klass: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  failed: { label: "Falhou", icon: XCircle, klass: "bg-rose-100 text-rose-700 border-rose-200" },
  cancelled: { label: "Cancelado", icon: XCircle, klass: "bg-slate-100 text-slate-500 border-slate-200" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const copyToClipboard = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copiado!`);
  } catch {
    toast.error("Não foi possível copiar.");
  }
};

// ─── Dialog: catálogo do GridProfile ───────────────────────────────────
const CatalogDialog = ({ open, onClose, candidateId, jobId, onAssigned }) => {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/integrations/gridprofile/catalog`)
      .then((res) => {
        if (!cancelled) setCatalog(res.data || []);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err?.response?.data?.detail || "Erro ao carregar catálogo do GridProfile",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const applyTest = async (testId) => {
    setAssigning(testId);
    try {
      const res = await axios.post(
        `${API}/candidates/${candidateId}/assessments`,
        { test_id: testId, job_id: jobId || "" },
      );
      toast.success("Teste enviado! Link disponível pra envio ao candidato.");
      onAssigned(res.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao aplicar teste");
    } finally {
      setAssigning("");
    }
  };

  const grouped = useMemo(() => {
    const out = { personalidade: [], raciocinio: [], tecnico: [] };
    catalog.forEach((t) => {
      const key = normalizeCategory(t.category);
      out[key].push(t);
    });
    return out;
  }, [catalog]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="assessment-catalog-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-600" />
            Catálogo de testes — GridProfile
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Escolha um teste pra aplicar. O candidato receberá um link único pra
            fazer a avaliação no GridProfile.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Carregando catálogo…
          </div>
        ) : catalog.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-6 text-center">
            Nenhum teste disponível no GridProfile no momento.
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {["personalidade", "raciocinio", "tecnico"].map((cat) => {
              const items = grouped[cat] || [];
              if (items.length === 0) return null;
              const meta = CATEGORY_BADGE[cat];
              return (
                <div key={cat}>
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2">
                    {meta.label}
                  </div>
                  <ul className="space-y-2">
                    {items.map((t) => {
                      const Icon = ICON_MAP[t.icon] || ICON_BY_TEST_ID[t.test_id] || Sparkles;
                      return (
                        <li
                          key={t.test_id}
                          data-testid={`catalog-item-${t.test_id}`}
                          className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3 hover:border-violet-300 transition-colors"
                        >
                          <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${meta.klass}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-slate-900">
                                {t.name}
                              </span>
                              {t.duration_minutes ? (
                                <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
                                  <Clock className="w-2.5 h-2.5" /> {t.duration_minutes}min
                                </span>
                              ) : null}
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                              {t.description}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => applyTest(t.test_id)}
                            disabled={!!assigning}
                            data-testid={`apply-test-${t.test_id}`}
                            className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold disabled:opacity-60"
                          >
                            {assigning === t.test_id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Plus className="w-3 h-3" />
                            )}
                            Aplicar
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Mapa de styles → nome legível por test_id. Usado pra renderizar
// "Perfil Principal: I - Influente" exatamente como o GridProfile.
const STYLE_LABELS = {
  "disc-classic": { D: "Dominante", I: "Influente", S: "Estável", C: "Conformidade" },
  "disc": { D: "Dominante", I: "Influente", S: "Estável", C: "Conformidade" },
};

const humanizeStyle = (testId, code) => {
  if (!code) return "";
  const map = STYLE_LABELS[testId] || {};
  const full = map[String(code).toUpperCase()];
  return full ? `${String(code).toUpperCase()} - ${full}` : String(code).toUpperCase();
};

// Chaves do result_summary que NÃO são scores numéricos por dimensão —
// pra filtrar antes de desenhar barras.
const META_KEYS = new Set([
  "primary_style",
  "secondary_style",
  "main_profile",
  "secondary_profile",
  "classificacao",
  "cefr_level",
  "level_name",
  "detected_level",
  "score_geral",
]);

// ─── Card de uma avaliação individual ──────────────────────────────────
const AssessmentCard = ({ a, candidate, onSync }) => {
  const [syncing, setSyncing] = useState(false);
  const meta = STATUS_META[a.status] || STATUS_META.pending;
  const StatusIcon = meta.icon;
  const completed = a.status === "completed";

  const sync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/assessments/${a.id}/sync`, {});
      toast.success("Status atualizado.");
      onSync();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const sendLinkWhatsApp = () => {
    if (!candidate?.phone || !a.public_link) return;
    const msg = encodeURIComponent(
      `Olá ${candidate.name?.split(" ")[0] || ""}! Você foi convidado(a) a fazer o teste "${a.test_name}" como parte do nosso processo seletivo. ` +
        `Acesse o link pra começar: ${a.public_link}`,
    );
    const phone = candidate.phone.replace(/\D/g, "");
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };

  return (
    <div
      data-testid={`assessment-card-${a.id}`}
      className="bg-white border border-slate-200 rounded-lg p-4 space-y-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-slate-900">{a.test_name}</h4>
            <span
              className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${meta.klass}`}
            >
              <StatusIcon
                className={`w-2.5 h-2.5 ${a.status === "in_progress" ? "animate-spin" : ""}`}
              />
              {meta.label}
            </span>
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            Aplicado por <strong>{a.applied_by_user_name || "—"}</strong> em{" "}
            {fmtDate(a.applied_at)}
            {a.completed_at && (
              <>
                {" · "}concluído em <strong>{fmtDate(a.completed_at)}</strong>
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={sync}
          disabled={syncing || completed}
          data-testid={`sync-${a.id}`}
          className="shrink-0 text-[10px] text-violet-700 hover:underline inline-flex items-center gap-0.5 disabled:opacity-40"
          title="Buscar de novo no GridProfile"
        >
          <RefreshCw className={`w-2.5 h-2.5 ${syncing ? "animate-spin" : ""}`} />
          Sincronizar
        </button>
      </div>

      {/* Score panel (when completed) */}
      {completed && (
        <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-md p-3 space-y-3">
          {/* Linha de cima: score grande + data conclusão */}
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-3xl font-bold text-emerald-700 tabular-nums">
              {a.score?.toFixed(1) ?? "—"}
              {a.score !== null && a.score !== undefined && (
                <span className="text-base font-medium text-emerald-600 ml-0.5">/100</span>
              )}
            </div>
            {a.completed_at && (
              <span
                className="text-[11px] text-emerald-700"
                data-testid={`completed-at-${a.id}`}
              >
                Realizado em <strong>{fmtDate(a.completed_at)}</strong>
              </span>
            )}
          </div>

          {/* Perfis principal/secundário — só renderiza quando vier do GridProfile */}
          {(a.result_summary?.primary_style || a.result_summary?.main_profile ||
            a.result_summary?.classificacao || a.result_summary?.cefr_level ||
            a.result_summary?.detected_level) && (
            <div
              className="grid grid-cols-2 gap-2"
              data-testid={`profile-block-${a.id}`}
            >
              {(a.result_summary.primary_style || a.result_summary.main_profile) && (
                <div className="bg-white border border-emerald-200 rounded p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    Perfil Principal
                  </div>
                  <div className="text-sm font-bold text-emerald-700 mt-0.5">
                    {humanizeStyle(
                      a.test_id,
                      a.result_summary.primary_style || a.result_summary.main_profile,
                    )}
                  </div>
                </div>
              )}
              {(a.result_summary.secondary_style || a.result_summary.secondary_profile) && (
                <div className="bg-white border border-emerald-200 rounded p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    Perfil Secundário
                  </div>
                  <div className="text-sm font-bold text-emerald-700 mt-0.5">
                    {humanizeStyle(
                      a.test_id,
                      a.result_summary.secondary_style || a.result_summary.secondary_profile,
                    )}
                  </div>
                </div>
              )}
              {a.result_summary.classificacao && (
                <div className="bg-white border border-emerald-200 rounded p-2 col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    Classificação
                  </div>
                  <div className="text-sm font-bold text-emerald-700 mt-0.5">
                    {a.result_summary.classificacao}
                  </div>
                </div>
              )}
              {a.result_summary.cefr_level && (
                <div className="bg-white border border-emerald-200 rounded p-2 col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    Nível CEFR
                  </div>
                  <div className="text-sm font-bold text-emerald-700 mt-0.5">
                    {a.result_summary.cefr_level}
                    {a.result_summary.level_name ? ` — ${a.result_summary.level_name}` : ""}
                  </div>
                </div>
              )}
              {a.result_summary.detected_level && !a.result_summary.cefr_level && (
                <div className="bg-white border border-emerald-200 rounded p-2 col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    Nível detectado
                  </div>
                  <div className="text-sm font-bold text-emerald-700 mt-0.5">
                    {a.result_summary.detected_level}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Barras horizontais — só os campos NUMÉRICOS do breakdown */}
          {a.result_summary && (() => {
            const numericEntries = Object.entries(a.result_summary).filter(
              ([k, v]) => typeof v === "number" && !META_KEYS.has(k),
            );
            if (numericEntries.length === 0) return null;
            return (
              <div
                className="space-y-1.5 pt-1"
                data-testid={`breakdown-bars-${a.id}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Pontuação detalhada
                </div>
                {numericEntries.map(([k, v]) => {
                  const pct = Math.max(0, Math.min(100, v));
                  const label = humanizeStyle(a.test_id, k) || k;
                  return (
                    <div key={k} className="flex items-center gap-2">
                      <div className="text-[11px] font-medium text-slate-700 w-28 shrink-0 truncate">
                        {label}
                      </div>
                      <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="text-[11px] font-bold text-emerald-700 tabular-nums w-10 text-right">
                        {pct.toFixed(1)}%
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {a.report_url && (
            <a
              href={a.report_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`report-${a.id}`}
              className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline font-medium"
            >
              <FileText className="w-3 h-3" /> Relatório completo
              <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}
        </div>
      )}

      {/* Public link panel (when not completed) */}
      {!completed && a.public_link && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
            Link pra o candidato
          </div>
          <div className="flex items-center gap-1.5">
            <code className="flex-1 text-[11px] text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 truncate">
              {a.public_link}
            </code>
            <button
              type="button"
              onClick={() => copyToClipboard(a.public_link, "Link")}
              data-testid={`copy-link-${a.id}`}
              className="shrink-0 p-1.5 rounded hover:bg-slate-200 text-slate-600"
              title="Copiar link"
            >
              <Copy className="w-3 h-3" />
            </button>
            {candidate?.phone && (
              <button
                type="button"
                onClick={sendLinkWhatsApp}
                data-testid={`send-wa-${a.id}`}
                className="shrink-0 inline-flex items-center gap-1 px-2 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-semibold"
                title="Abrir WhatsApp"
              >
                <Send className="w-3 h-3" />
                WhatsApp
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Componente principal ──────────────────────────────────────────────
const CandidateAssessmentsPanel = ({ candidate, jobId = "" }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    if (!candidate?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/candidates/${candidate.id}/assessments`);
      setItems(res.data || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate?.id]);

  // Auto-refresh enquanto houver testes pending/in_progress.
  // Cobre o caso de webhooks atrasados ou que falharam silenciosamente.
  // Pára automaticamente quando todos os testes estão completed/failed.
  useEffect(() => {
    const hasInFlight = items.some((a) =>
      ["pending", "in_progress"].includes(a.status),
    );
    if (!hasInFlight) return undefined;
    const interval = setInterval(() => {
      load();
    }, 20000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, candidate?.id]);

  return (
    <div data-testid="assessments-panel" className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Avaliações do GridProfile
          </h3>
          <p className="text-[11px] text-slate-500">
            Testes psicométricos e técnicos aplicados a este candidato
          </p>
        </div>
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          data-testid="open-catalog-button"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold"
        >
          <Plus className="w-3 h-3" />
          Aplicar novo teste
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-500 py-4">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando…
        </div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
          <Sparkles className="w-8 h-8 text-violet-300 mx-auto mb-2" />
          <p className="text-sm text-slate-600">
            Nenhum teste aplicado ainda.
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            Clique em <strong>Aplicar novo teste</strong> pra escolher do catálogo.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <AssessmentCard key={a.id} a={a} candidate={candidate} onSync={load} />
          ))}
        </div>
      )}

      <CatalogDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        candidateId={candidate?.id}
        jobId={jobId}
        onAssigned={() => load()}
      />
    </div>
  );
};

export default CandidateAssessmentsPanel;
